import {
	BankOutlined,
	ClockCircleOutlined,
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
	SaveOutlined,
	TagOutlined,
	UserAddOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Checkbox,
	Col,
	Collapse,
	DatePicker,
	Divider,
	Form,
	Input,
	Modal,
	Row,
	Select,
	Spin,
	Table,
	Tag,
} from "antd";
import dayjs from "dayjs";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { getAperturaActiva } from "../../services/cajaEstadoService";
import { getAllCajas } from "../../services/cajaService";
import { getValidCampaignsForDate } from "../../services/campaignService";
import { getClientAddressesByNo } from "../../services/clientAddressService";
import { getAllClients } from "../../services/clientService";
import { getAllColegios } from "../../services/colegioService";
import { getValidCouponDetailsForInvoice } from "../../services/couponValidationService";
import {
	addInvoicePaymentLine,
	deleteInvoicePaymentLine,
	getInvoicePaymentLines,
	updateInvoicePaymentLine,
} from "../../services/invoicePaymentService";
import {
	addInvoice,
	addInvoiceLine,
	convertDraftToInvoice,
	getInvoiceLines,
	saveInvoiceAsDraft,
	updateInvoice,
} from "../../services/invoiceService";
import { hasProductLines } from "../../services/productLinesService";
import { getAllProducts } from "../../services/productService";
import { getTPVByNoOrId } from "../../services/tpvService";
import { getAllTurnos } from "../../services/turnoService";
import ClientForm from "../Client/ClientForm";
import InvoiceLines from "./InvoiceLines";
import InvoicePaymentLineForm from "./InvoicePaymentLineForm";
import "./InvoiceStyles.css";
import InvoiceTicket from "./InvoiceTicket";

const { Option } = Select;

// Componente interno para manejar los campos de tipo de documento con watch
const ConfirmationFormFields = ({ form }) => {
	// Observar cambios en el tipo de documento SrI para cambiar labels dinámicamente
	const tipoDocumentoSrI = Form.useWatch("Tipo_Documento_SrI", form);

	return (
		<Row gutter={16}>
			<Col span={8}>
				<Form.Item
					name="Tipo_Documento_SrI"
					label="Tipo de documento"
					rules={[{ required: true, message: "Este campo es obligatorio" }]}
				>
					<Select
						placeholder="Tipo"
						options={[
							{ value: "RUC", label: "RUC" },
							{ value: "Cedula", label: "Cédula" },
							{ value: "Pasaporte", label: "Pasaporte" },
						]}
					/>
				</Form.Item>
			</Col>
			<Col span={16}>
				<Form.Item
					name="VAT_Registration_No_"
					label={
						tipoDocumentoSrI === "RUC"
							? "RUC"
							: tipoDocumentoSrI === "Cedula"
								? "Cédula"
								: tipoDocumentoSrI === "Pasaporte"
									? "Pasaporte"
									: "Número de identificación"
					}
					rules={[
						{
							required: true,
							message: `Ingrese el ${tipoDocumentoSrI === "RUC"
								? "RUC"
								: tipoDocumentoSrI === "Cedula"
									? "número de cédula"
									: tipoDocumentoSrI === "Pasaporte"
										? "número de pasaporte"
										: "número de identificación"
								}`,
						},
					]}
				>
					<Input />
				</Form.Item>
			</Col>
		</Row>
	);
};

const InvoiceForm = ({ invoice, onSave, onCancel }) => {
	const [form] = Form.useForm();
	const [confirmForm] = Form.useForm();
	const [products, setProducts] = useState();
	const [invoiceTotals, setInvoiceTotals] = useState({
		subtotal: 0,
		vat: 0,
		total: 0,
	});
	const [clients, setClients] = useState();
	const [clientAddresses, setClientAddresses] = useState([]);
	const [colegios, setColegios] = useState();
	const [cajas, setCajas] = useState();
	const [turnos, setTurnos] = useState();
	const [lines, setLines] = useState([]);
	const [paymentLines, setPaymentLines] = useState([]);
	const [selectedPaymentLine, setSelectedPaymentLine] = useState(null);
	const [paymentLineModalVisible, setPaymentLineModalVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const [lineLoading, setLineLoading] = useState(false);
	const [paymentLineLoading, setPaymentLineLoading] = useState(false);
	const [selectedClientDiscount, setSelectedClientDiscount] = useState(0);
	const [confirmationModalVisible, setConfirmationModalVisible] =
		useState(false);
	const [confirmationData, setConfirmationData] = useState({});
	const [ticketModalVisible, setTicketModalVisible] = useState(false);
	const [createdInvoice, setCreatedInvoice] = useState(null);
	const [clientModalVisible, setClientModalVisible] = useState(false);
	const [isNewClient, setIsNewClient] = useState(false);
	const [aperturaActiva, setAperturaActiva] = useState(null);
	const [cajaNombre, setCajaNombre] = useState("");
	const [tpv, setTpv] = useState(null);
	const [turnoNombre, setTurnoNombre] = useState("");
	const [availableCouponDetails, setAvailableCouponDetails] = useState([]);
	const [selectedCouponDetail, setSelectedCouponDetail] = useState(null);
	const [loadingCoupons, setLoadingCoupons] = useState(false);
	const loadedCoupons = useRef(false);
	const [campaigns, setCampaigns] = useState([]);
	const [loadingCampaigns] = useState(false);
	const loadedCampaigns = useRef(false);
	const [initialDataLoaded, setInitialDataLoaded] = useState(false);
	const [hasKitsInLines, setHasKitsInLines] = useState(false);

	// Observar cambios en el tipo de documento para renderizado condicional
	const documentType = Form.useWatch("Document_Type", form);
	// Observar cambios en el tipo de documento SrI para cambiar labels dinámicamente
	const tipoDocumentoSrI = Form.useWatch("Tipo_Documento_SrI", form);

	const isInvoiceEditing = !!invoice?.id;
	const isSynchronized = !!invoice?.No;
	const isDraft = invoice?.isDraft || false;

	// Función para determinar si una campaña está activa
	const isCampaignActive = useCallback((campaign) => {
		const today = new Date();

		// Si no hay fechas definidas, se considera activa
		if (!campaign.Starting_Date && !campaign.Ending_Date) {
			return true;
		}

		// Verificar si está en el rango de fechas
		let startDate = null;
		let endDate = null;

		if (campaign.Starting_Date) {
			const parts = campaign.Starting_Date.split("/");
			if (parts.length === 3) {
				const day = parseInt(parts[0]);
				const month = parseInt(parts[1]) - 1;
				let year = parseInt(parts[2]);
				if (year < 100) {
					year = year <= 30 ? 2000 + year : 1900 + year;
				}
				startDate = new Date(year, month, day);
			}
		}

		if (campaign.Ending_Date) {
			const parts = campaign.Ending_Date.split("/");
			if (parts.length === 3) {
				const day = parseInt(parts[0]);
				const month = parseInt(parts[1]) - 1;
				let year = parseInt(parts[2]);
				if (year < 100) {
					year = year <= 30 ? 2000 + year : 1900 + year;
				}
				endDate = new Date(year, month, day);
			}
		}

		if (startDate && today < startDate) return false;
		if (endDate && today > endDate) return false;

		return true;
	}, []);

	// Función para seleccionar la campaña por defecto
	const selectDefaultCampaign = useCallback(
		(campaignList) => {
			if (!campaignList || campaignList.length === 0) return;

			// Filtrar campañas activas
			const activeCampaigns = campaignList.filter((campaign) =>
				isCampaignActive(campaign)
			);

			if (activeCampaigns.length > 0) {
				// Ordenar campañas por fecha de inicio (más reciente primero)
				const sortedCampaigns = activeCampaigns.sort((a, b) => {
					// Función helper para convertir fecha DD/MM/YY a Date
					const parseDate = (dateString) => {
						if (!dateString) return new Date(0); // Si no hay fecha, usar una fecha muy antigua

						const parts = dateString.split("/");
						if (parts.length === 3) {
							const day = parseInt(parts[0]);
							const month = parseInt(parts[1]) - 1;
							let year = parseInt(parts[2]);
							if (year < 100) {
								year = year <= 30 ? 2000 + year : 1900 + year;
							}
							return new Date(year, month, day);
						}
						return new Date(0);
					};

					const dateA = parseDate(a.Starting_Date);
					const dateB = parseDate(b.Starting_Date);

					// Ordenar de más reciente a más antigua (descendente)
					return dateB.getTime() - dateA.getTime();
				});

				// Tomar la campaña más reciente (primera en la lista ordenada)
				const defaultCampaign = sortedCampaigns[0];

				// Solo establecer si no hay una campaña ya seleccionada y no estamos editando una factura
				if (!form.getFieldValue("Campaign_No_") && !isInvoiceEditing) {
					form.setFieldValue("Campaign_No_", defaultCampaign.No);
					console.log(
						`Campaña seleccionada por defecto (más reciente): ${defaultCampaign.No
						} - ${defaultCampaign.Description || "Sin descripción"} (Inicio: ${defaultCampaign.Starting_Date || "Sin fecha"
						})`
					);
				}
			}
		},
		[form, isCampaignActive, isInvoiceEditing]
	);

	// Función optimizada para cargar todos los datos iniciales en paralelo
	const loadInitialData = useCallback(async () => {
		if (initialDataLoaded) return;

		try {
			setLoading(true);
			console.log("Iniciando carga de datos iniciales...");

			// Cargar todos los datos en paralelo
			const [
				clientList,
				colegioList,
				cajaList,
				turnoList,
				productList,
				apertura,
				validCampaigns,
			] = await Promise.all([
				getAllClients(),
				getAllColegios(),
				getAllCajas(),
				getAllTurnos(),
				getAllProducts(),
				getAperturaActiva(),
				getValidCampaignsForDate(),
			]);

			console.log("Datos cargados exitosamente");

			// Actualizar todos los estados de una vez
			setClients(clientList);
			setColegios(colegioList);
			setCajas(cajaList);
			setTurnos(turnoList);
			setProducts(productList);
			setAperturaActiva(apertura);
			setCampaigns(validCampaigns);
			loadedCampaigns.current = true;

			// Seleccionar campaña por defecto basada en fecha actual
			selectDefaultCampaign(validCampaigns);

			// Configurar datos de caja y turno
			if (apertura && cajaList && turnoList) {
				const cajaSel = cajaList.find((c) => c.No === apertura.Caja);
				const turnoSel = turnoList.find(
					(t) =>
						t.No === apertura.Turno || t.DescripcionTurno === apertura.Turno
				);

				setCajaNombre(cajaSel ? cajaSel.nombre : apertura.Caja || "");
				setTpv(cajaSel ? cajaSel.TPV : null);
				setTurnoNombre(
					turnoSel ? turnoSel.DescripcionTurno : apertura.Turno || ""
				);

				// Actualizar el formulario con la caja y turno actuales
				form.setFieldsValue({
					caja: apertura.Caja,
					turno: turnoSel ? turnoSel.No : apertura.Turno,
				});
			} else if (!apertura) {
				window.messageApi.warning(
					"No hay una caja abierta. Debe abrir una caja para crear facturas."
				);
			}

			setInitialDataLoaded(true);
			console.log("Carga inicial completada");
		} catch (error) {
			console.error("Error al cargar datos iniciales:", error);
			window.messageApi.error(
				"Error al cargar datos. Por favor, inténtelo de nuevo."
			);
		} finally {
			setLoading(false);
		}
	}, [initialDataLoaded, form, selectDefaultCampaign]);

	// Cargar datos iniciales solo una vez
	useEffect(() => {
		loadInitialData();
	}, [loadInitialData]);

	// Función para cargar cupones válidos (optimizada)
	const loadValidCoupons = useCallback(async () => {
		try {
			if (
				loadedCoupons.current ||
				!cajas ||
				!aperturaActiva ||
				!tpv ||
				!clients
			) {
				return;
			}

			loadedCoupons.current = true;
			setLoadingCoupons(true);

			const formValues = form.getFieldsValue();
			const selectedClient = clients.find(
				(c) => c.id === formValues.Sell_to_Customer_No_
			);

			const invoiceData = {
				caja: formValues.caja || aperturaActiva?.Caja,
				Colegio: formValues.Colegio,
				...formValues,
			};

			const { allCouponDetails } = await getValidCouponDetailsForInvoice(
				invoiceData,
				selectedClient,
				tpv
			);

			setAvailableCouponDetails(allCouponDetails);
		} catch (error) {
			console.error("Error al cargar cupones válidos:", error);
			window.messageApi.error("Error al cargar cupones disponibles");
		} finally {
			setLoadingCoupons(false);
		}
	}, [form, clients, aperturaActiva, cajas, tpv]);

	// Cargar cupones válidos cuando estén disponibles todos los datos necesarios
	useEffect(() => {
		if (
			initialDataLoaded &&
			clients &&
			cajas &&
			aperturaActiva &&
			tpv !== null &&
			!invoice?.Cupon
		) {
			loadValidCoupons();
		}
	}, [
		initialDataLoaded,
		clients,
		cajas,
		aperturaActiva,
		tpv,
		invoice?.Cupon,
		loadValidCoupons,
	]);

	// Función para verificar si hay kits en las líneas de factura
	const checkForKitsInLines = useCallback(async () => {
		if (!lines || lines.length === 0) {
			setHasKitsInLines(false);
			return;
		}

		try {
			let foundKit = false;

			for (const line of lines) {
				if (line.No && line.key !== "new") {
					const hasLines = await hasProductLines(line.No);
					if (hasLines) {
						foundKit = true;
						break;
					}
				}
			}

			setHasKitsInLines(foundKit);

			// Si encontramos un kit y no es una factura en edición, activar automáticamente el checkbox
			if (foundKit && !isInvoiceEditing) {
				form.setFieldValue("libros_sueltos", true);
			}
		} catch (error) {
			console.error("Error al verificar kits en líneas:", error);
			setHasKitsInLines(false);
		}
	}, [lines, form, isInvoiceEditing]);

	// Verificar kits cada vez que cambien las líneas
	useEffect(() => {
		checkForKitsInLines();
	}, [checkForKitsInLines]);

	// useEffect para cargar líneas de factura y pago cuando se edita una factura existente (optimizado)
	useEffect(() => {
		const loadInvoiceData = async () => {
			if (!isInvoiceEditing || !invoice?.id) return;

			try {
				setLineLoading(true);
				setPaymentLineLoading(true);

				// Cargar líneas de factura y pago en paralelo
				const [lineList, paymentLineList] = await Promise.all([
					getInvoiceLines(invoice.id),
					getInvoicePaymentLines(invoice.id),
				]);

				setLines(lineList);
				setPaymentLines(paymentLineList);
			} catch (error) {
				console.error("Error al cargar datos de la factura:", error);
				window.messageApi.error(
					"No se pudieron cargar los datos de la factura"
				);
			} finally {
				setLineLoading(false);
				setPaymentLineLoading(false);
			}
		};

		loadInvoiceData();
	}, [isInvoiceEditing, invoice?.id]);

	// useEffect para configurar líneas predefinidas en facturas nuevas (ej: abonos)
	useEffect(() => {
		if (!isInvoiceEditing && invoice?.lines) {
			setLines(invoice.lines);
		}
		if (!isInvoiceEditing && invoice?.paymentLines) {
			setPaymentLines(invoice.paymentLines);
		}
	}, [isInvoiceEditing, invoice?.lines, invoice?.paymentLines]);

	// useEffect para configurar datos básicos del formulario y cliente (optimizado)
	useEffect(() => {
		if (!invoice) return;

		const isNewCreditMemo = !invoice.id && invoice.Document_Type === "Credit Memo";

		const formattedInvoice = {
			...invoice,
			// Para notas de crédito nuevas, usar siempre fechas actuales
			// Para otras facturas, usar las fechas existentes o actuales si no hay
			Posting_Date: isNewCreditMemo ? dayjs() : (invoice.Posting_Date
				? dayjs(invoice.Posting_Date)
				: dayjs()),
			Document_Date: isNewCreditMemo ? dayjs() : (invoice.Document_Date
				? dayjs(invoice.Document_Date)
				: dayjs()),
			Document_Type: invoice.Document_Type || "Invoice",
			caja: invoice.caja || "",
			turno: invoice.turno || "",
		};

		form.setFieldsValue(formattedInvoice);

		// Configurar descuento del cliente si está disponible
		if (invoice.Sell_to_Customer_No_ && clients) {
			const selectedClient = clients.find(
				(c) =>
					c.id === invoice.Sell_to_Customer_No_ ||
					c.No === invoice.Sell_to_Customer_No_
			);

			if (selectedClient) {
				setSelectedClientDiscount(selectedClient.POS_Discount || 0);
				setIsNewClient(!selectedClient.No);

				// Asegurar que los datos del cliente estén en el formulario, incluyendo Direccion_2
				form.setFieldsValue({
					Bill_to_Name: invoice.Bill_to_Name || selectedClient.name,
					Bill_to_Address: invoice.Bill_to_Address || selectedClient.Direccion || "",
					Direccion_2: invoice.Direccion_2 || selectedClient.Direccion_2 || "",
					Bill_to_City: invoice.Bill_to_City || selectedClient.Poblacion || "",
					VAT_Registration_No_: invoice.VAT_Registration_No_ || selectedClient.Numero_Identificacion_fiscal || "",
					Bill_to_Country_Region_Code: invoice.Bill_to_Country_Region_Code || selectedClient.Pais || "",
					Tipo_Documento_SrI: invoice.Tipo_Documento_SrI || selectedClient.Tipo_Documento_SrI || "Cedula",
					E_Mail: invoice.E_Mail || selectedClient.E_Mail || "",
					Telefono: invoice.Telefono || selectedClient.Telefono || "",
					Mobil: invoice.Mobil || selectedClient.Mobil || "",
				});

				// Cargar direcciones del cliente si tiene No (está sincronizado)
				if (selectedClient.No) {
					getClientAddressesByNo(selectedClient.No)
						.then((addresses) => {
							setClientAddresses(addresses);

							// Si es una factura nueva y solo tiene una dirección, seleccionarla automáticamente
							// Para facturas existentes, mantener la dirección ya seleccionada
							if (!isInvoiceEditing && addresses.length === 1) {
								form.setFieldValue("Ship_to_Code", addresses[0].Code);
							}
						})
						.catch((error) => {
							console.error("Error al cargar direcciones del cliente:", error);
							setClientAddresses([]);
						});
				} else {
					setClientAddresses([]);
				}
			}
		}
	}, [invoice, clients, form, isInvoiceEditing]);

	// useEffect para configurar cupón existente en facturas editadas (optimizado)
	useEffect(() => {
		const setupExistingCoupon = async () => {
			if (!invoice?.Cupon || !invoice?.Tipo_Detalle || !invoice?.No_Detalle)
				return;
			if (!initialDataLoaded || !clients || !cajas || !aperturaActiva) return;

			try {
				const detailKey = `${invoice.Cupon}-${invoice.Tipo_Detalle}-${invoice.No_Detalle}`;

				// Si ya tenemos el cupón configurado, no hacer nada
				if (selectedCouponDetail?.key === detailKey) return;

				const invoiceData = {
					caja: invoice.caja || aperturaActiva?.Caja,
					Colegio: invoice.Colegio,
					...invoice,
				};
				const selectedClient = clients.find(
					(c) =>
						c.id === invoice.Sell_to_Customer_No_ ||
						c.No === invoice.Sell_to_Customer_No_
				);

				const { allCouponDetails } = await getValidCouponDetailsForInvoice(
					invoiceData,
					selectedClient,
					tpv
				);
				const specificCouponDetail = allCouponDetails.find(
					(c) => c.key === detailKey
				);

				const couponDetail = specificCouponDetail || {
					key: detailKey,
					couponNo: invoice.Cupon,
					detailType: invoice.Tipo_Detalle,
					detailNo: invoice.No_Detalle,
					isValid: false,
					originalCoupon: { Line_Discounts: [] },
				};

				setSelectedCouponDetail(couponDetail);
				form.setFieldsValue({
					couponDetail: detailKey,
					Cupon: invoice.Cupon,
					Tipo_Detalle: invoice.Tipo_Detalle,
					No_Detalle: invoice.No_Detalle,
				});
			} catch (error) {
				console.warn("Error al cargar información del cupón:", error);
				const detailKey = `${invoice.Cupon}-${invoice.Tipo_Detalle}-${invoice.No_Detalle}`;
				setSelectedCouponDetail({
					key: detailKey,
					couponNo: invoice.Cupon,
					detailType: invoice.Tipo_Detalle,
					detailNo: invoice.No_Detalle,
					isValid: false,
					originalCoupon: { Line_Discounts: [] },
				});

				form.setFieldsValue({
					couponDetail: detailKey,
					Cupon: invoice.Cupon,
					Tipo_Detalle: invoice.Tipo_Detalle,
					No_Detalle: invoice.No_Detalle,
				});
			}
		};

		// Solo ejecutar si es una factura con cupón existente y todos los datos están cargados
		if (
			invoice?.Cupon &&
			initialDataLoaded &&
			clients &&
			cajas &&
			aperturaActiva &&
			tpv !== null
		) {
			setupExistingCoupon();
		}
	}, [
		invoice,
		initialDataLoaded,
		clients,
		cajas,
		aperturaActiva,
		tpv,
		selectedCouponDetail?.key,
		form,
	]);

	// Todos los hooks deben ir antes del return condicional
	const handleClientChange = useCallback(
		async (value) => {
			const client = clients.find((c) => c.id === value);
			const tpvInfo = await getTPVByNoOrId(tpv);
			if (client) {
				form.setFieldsValue({
					Bill_to_Name: client.name,
					Bill_to_Address: client.Direccion || "",
					Direccion_2: client.Direccion_2 || "",
					Bill_to_City: client.Poblacion || "",
					VAT_Registration_No_: client.Numero_Identificacion_fiscal || "",
					Bill_to_Country_Region_Code: client.Pais || tpvInfo.pais || "",
					Tipo_Documento_SrI: client.Tipo_Documento_SrI || "Cedula",
					E_Mail: client.E_Mail || "",
					Telefono: client.Telefono || "",
					Mobil: client.Mobil || "",
					Ship_to_Code: "", // Limpiar dirección de envío al cambiar cliente
				});
				// Guardar el descuento del cliente seleccionado
				setSelectedClientDiscount(client.POS_Discount || 0);
				// Si es un cliente nuevo sin sincronizar, marcarlo
				setIsNewClient(!client.No);

				// Cargar direcciones del cliente si tiene No (está sincronizado)
				if (client.No) {
					try {
						const addresses = await getClientAddressesByNo(client.No);
						setClientAddresses(addresses);

						// Si solo tiene una dirección, seleccionarla automáticamente
						if (addresses.length === 1) {
							form.setFieldValue("Ship_to_Code", addresses[0].Code);
						}
					} catch (error) {
						console.error("Error al cargar direcciones del cliente:", error);
						setClientAddresses([]);
					}
				} else {
					setClientAddresses([]);
				}

				// Resetear cupones para recargar con nuevo cliente
				loadedCoupons.current = false;
				setAvailableCouponDetails([]);
				setSelectedCouponDetail(null);

				// Limpiar campos de cupón
				form.setFieldsValue({
					couponDetail: null,
					Cupon: null,
					Tipo_Detalle: null,
					No_Detalle: null,
				});
			}
		},
		[clients, form, tpv]
	);

	// Función para manejar cambios en el colegio (recargar cupones)
	const handleColegioChange = useCallback(
		(colegioNo) => {
			form.setFieldsValue({ Colegio: colegioNo });

			// Solo resetear cupones si hay un cliente seleccionado
			if (form.getFieldValue("Sell_to_Customer_No_")) {
				loadedCoupons.current = false;
				setAvailableCouponDetails([]);
				setSelectedCouponDetail(null);

				// Limpiar campos de cupón
				form.setFieldsValue({
					couponDetail: null,
					Cupon: null,
					Tipo_Detalle: null,
					No_Detalle: null,
				});
			}
		},
		[form]
	);

	// Calcular totales de forma optimizada con useMemo
	const paymentTotal = useMemo(() => {
		return paymentLines.reduce((sum, line) => sum + (line.Amount || 0), 0);
	}, [paymentLines]);

	const paymentMethodDiscount = useMemo(() => {
		// Si no hay líneas de pago, no hay descuento
		if (!paymentLines.length) return 0;
		// Si hay más de una línea de pago, no aplicar descuento
		if (paymentLines.length > 1) return 0;
		// Solo si hay exactamente una línea de pago, aplicar su descuento
		return paymentLines[0].Dto || 0;
	}, [paymentLines]);

	const finalTotals = useMemo(() => {
		const { subtotal, vat, total } = invoiceTotals;

		// Aplicar descuento de forma de pago al total
		let finalTotal = total;
		if (paymentMethodDiscount) {
			const discountAmount = total * (paymentMethodDiscount / 100);
			finalTotal = total - discountAmount;
		}

		return {
			subtotal,
			vat,
			total: finalTotal,
			paymentMethodDiscount,
		};
	}, [invoiceTotals, paymentMethodDiscount]);

	// Funciones helper que usan los valores memoizados
	const calculateTotals = () => finalTotals;
	const calculatePaymentTotal = () => paymentTotal;
	const calculateDiscountFromPaymentMethod = () => paymentMethodDiscount;
	const applyPaymentMethodDiscount = (total) => {
		if (!paymentMethodDiscount) return total;
		const discountAmount = total * (paymentMethodDiscount / 100);
		return total - discountAmount;
	};

	// Comparación con tolerancia para evitar problemas de redondeo
	const isApproximatelyZero = (value, tolerance = 0.01) => {
		return Math.abs(value) <= tolerance;
	};

	if (!initialDataLoaded) {
		return (
			<div style={{ padding: "50px", textAlign: "center" }}>
				<Spin size="large" tip="Cargando datos del formulario..." />
			</div>
		);
	}

	// Función para manejar la creación de un nuevo cliente
	const handleAddClient = () => {
		setClientModalVisible(true);
	};

	// Función para manejar cuando se guarda un nuevo cliente
	const handleClientAdded = (newClient) => {
		// Cerrar el modal
		setClientModalVisible(false);

		// Actualizar la lista de clientes
		setClients([...clients, newClient]);

		// Seleccionar el nuevo cliente en el formulario
		// Si el cliente tiene No (sincronizado), usar ese No
		// Si no tiene No (cliente local), usar el ID local
		const clientIdentifier = newClient.No || newClient.id;

		form.setFieldsValue({
			Sell_to_Customer_No_: clientIdentifier,
			Bill_to_Name: newClient.name,
			Bill_to_Address: newClient.Direccion || "",
			Direccion_2: newClient.Direccion_2 || "",
			Bill_to_City: newClient.Poblacion || "",
			VAT_Registration_No_: newClient.Numero_Identificacion_fiscal || "",
			Bill_to_Country_Region_Code: newClient.Pais || "",
			Tipo_Documento_SrI: newClient.Tipo_Documento_SrI || "Cedula",
			E_Mail: newClient.E_Mail || "",
			Telefono: newClient.Telefono || "",
			Mobil: newClient.Mobil || "",
			Ship_to_Code: "", // Nuevo cliente no tiene direcciones
		});

		// Guardar el descuento del cliente
		setSelectedClientDiscount(newClient.POS_Discount || 0);

		// Marcar como cliente nuevo no sincronizado solo si no tiene No
		setIsNewClient(!newClient.No);

		// Limpiar direcciones ya que es un cliente nuevo
		setClientAddresses([]);

		window.messageApi.success("Cliente añadido y seleccionado correctamente");

		// Cargar cupones válidos para el nuevo cliente
		loadValidCoupons();
	};

	// Función para manejar la selección de cupón
	const handleCouponChange = (detailKey) => {
		if (!detailKey) {
			setSelectedCouponDetail(null);
			form.setFieldsValue({
				Cupon: null,
				Tipo_Detalle: null,
				No_Detalle: null,
			});
			return;
		}

		const couponDetail = availableCouponDetails.find(
			(c) => c.key === detailKey
		);
		if (couponDetail && couponDetail.isValid) {
			setSelectedCouponDetail(couponDetail);

			// Guardar los tres campos requeridos
			form.setFieldsValue({
				Cupon: couponDetail.couponNo,
				Tipo_Detalle: couponDetail.detailType,
				No_Detalle: couponDetail.detailNo,
			});

			window.messageApi.success(
				`Cupón ${couponDetail.couponDescription} (${couponDetail.detailType}: ${couponDetail.detailNo}) seleccionado`
			);
		} else {
			window.messageApi.warning(
				"El detalle de cupón seleccionado no es válido para esta factura"
			);
		}
	};

	const handleSubmit = async (values) => {
		try {
			// Verificar que haya una apertura de caja activa
			if (!aperturaActiva && !isInvoiceEditing) {
				window.messageApi.error(
					"No hay una caja abierta. Debe abrir una caja para crear facturas."
				);
				return;
			}

			// Verificar que las notas de crédito tengan pendiente 0.00 (con tolerancia)
			if (values.Document_Type === "Credit Memo") {
				const pendingAmount =
					Math.abs(applyPaymentMethodDiscount(invoiceTotals.total)) -
					calculatePaymentTotal();
				if (!isApproximatelyZero(pendingAmount)) {
					window.messageApi.error(
						`No se puede crear una nota de crédito con importe pendiente. El pendiente actual es ${pendingAmount.toFixed(
							2
						)} $. Debe ajustar los pagos para que el pendiente sea exactamente 0.00.`
					);
					return;
				}
			}

			// Usar el messageApi global para mostrar mensajes
			window.messageApi.loading("Preparando confirmación...", 1);

			// Asegurarnos de que se usan los valores de la apertura activa
			if (aperturaActiva && !isInvoiceEditing) {
				values.caja = aperturaActiva.Caja;
				// Encontrar el turno correcto y usar su No
				if (turnos) {
					const turnoSel = turnos.find(
						(t) =>
							t.No === aperturaActiva.Turno ||
							t.DescripcionTurno === aperturaActiva.Turno
					);
					values.turno = turnoSel ? turnoSel.No : aperturaActiva.Turno;
				} else {
					values.turno = aperturaActiva.Turno;
				}
			}

			// Obtener los datos del cliente seleccionado para asegurar que están completos
			const selectedClient = clients.find(
				(c) => c.id === values.Sell_to_Customer_No_ || c.No === values.Sell_to_Customer_No_
			);

			// Debug: verificar si se encontró el cliente
			if (!selectedClient && values.Sell_to_Customer_No_) {
				console.warn("Cliente no encontrado en la lista:", values.Sell_to_Customer_No_, "Usando datos del formulario");
			}

			// Asegurar que los datos del cliente están completos, tanto en values como en el formulario de confirmación
			// Priorizar los datos del formulario (que ya están completos para notas de crédito) sobre los del cliente
			const clientData = {
				Bill_to_Name: values.Bill_to_Name || selectedClient?.name || "",
				Bill_to_Address:
					values.Bill_to_Address || selectedClient?.Direccion || "",
				Direccion_2: values.Direccion_2 || selectedClient?.Direccion_2 || "",
				Bill_to_City: values.Bill_to_City || selectedClient?.Poblacion || "",
				VAT_Registration_No_:
					values.VAT_Registration_No_ ||
					selectedClient?.Numero_Identificacion_fiscal ||
					"",
				Bill_to_Country_Region_Code:
					values.Bill_to_Country_Region_Code || selectedClient?.Pais || "",
				Tipo_Documento_SrI:
					values.Tipo_Documento_SrI ||
					selectedClient?.Tipo_Documento_SrI ||
					"Cedula",
			};

			// Combinar todos los valores con los datos del cliente
			const completeValues = {
				...values,
				...clientData,
			};

			// Mostrar modal de confirmación de datos para cumplir con requisitos legales
			setConfirmationData(completeValues);
			confirmForm.setFieldsValue({
				...clientData,
				Ship_to_Code: completeValues.Ship_to_Code,
			});
			setConfirmationModalVisible(true);
		} catch (error) {
			console.error("Error al preparar confirmación:", error);
			window.messageApi.error("No se pudo preparar la confirmación de datos");
		}
	};

	const handleSaveAsDraft = async () => {
		try {
			// Verificar que haya una apertura de caja activa
			if (!aperturaActiva && !isInvoiceEditing) {
				window.messageApi.error(
					"No hay una caja abierta. Debe abrir una caja para crear facturas."
				);
				return;
			}

			const values = form.getFieldsValue();

			// Verificar que las notas de crédito tengan pendiente 0.00 (con tolerancia, incluso para borradores)
			if (values.Document_Type === "Credit Memo") {
				const pendingAmount =
					Math.abs(applyPaymentMethodDiscount(invoiceTotals.total)) -
					calculatePaymentTotal();
				if (!isApproximatelyZero(pendingAmount)) {
					window.messageApi.error(
						`No se puede guardar una nota de crédito con importe pendiente. El pendiente actual es ${pendingAmount.toFixed(
							2
						)} $. Debe ajustar los pagos para que el pendiente sea exactamente 0.00.`
					);
					return;
				}
			}

			setLoading(true);

			// Asegurarnos de que se usan los valores de la apertura activa
			if (aperturaActiva && !isInvoiceEditing) {
				values.caja = aperturaActiva.Caja;
				// Encontrar el turno correcto y usar su No
				if (turnos) {
					const turnoSel = turnos.find(
						(t) =>
							t.No === aperturaActiva.Turno ||
							t.DescripcionTurno === aperturaActiva.Turno
					);
					values.turno = turnoSel ? turnoSel.No : aperturaActiva.Turno;
				} else {
					values.turno = aperturaActiva.Turno;
				}
			}

			// Obtener los datos del cliente seleccionado para asegurar que están completos
			const selectedClient = clients.find(
				(c) => c.id === values.Sell_to_Customer_No_ || c.No === values.Sell_to_Customer_No_
			);

			// Debug: verificar si se encontró el cliente
			if (!selectedClient && values.Sell_to_Customer_No_) {
				console.warn("Cliente no encontrado en la lista:", values.Sell_to_Customer_No_, "Usando datos del formulario");
			}

			// Asegurar que los datos del cliente están completos
			// Priorizar los datos del formulario (que ya están completos para notas de crédito) sobre los del cliente
			const clientData = {
				Bill_to_Name: values.Bill_to_Name || selectedClient?.name || "",
				Bill_to_Address:
					values.Bill_to_Address || selectedClient?.Direccion || "",
				Direccion_2: values.Direccion_2 || selectedClient?.Direccion_2 || "",
				Bill_to_City: values.Bill_to_City || selectedClient?.Poblacion || "",
				VAT_Registration_No_:
					values.VAT_Registration_No_ ||
					selectedClient?.Numero_Identificacion_fiscal ||
					"",
				Bill_to_Country_Region_Code:
					values.Bill_to_Country_Region_Code || selectedClient?.Pais || "",
				Tipo_Documento_SrI:
					values.Tipo_Documento_SrI ||
					selectedClient?.Tipo_Documento_SrI ||
					"Cedula",
				Ship_to_Code: values.Ship_to_Code || "",
			};

			// Formatear datos de la factura
			const invoiceData = {
				...values,
				...clientData,
				Document_Type: values.Document_Type || "Invoice",
				Posting_Date: values.Posting_Date?.format("YYYY-MM-DD"),
				Document_Date: values.Document_Date?.format("YYYY-MM-DD"),
				// Para abonos, asegurarse de guardar el tipo de documento corregido
				Applies_to_Doc_Type:
					values.Document_Type === "Credit Memo" ? "Invoice" : undefined,
				// Guardar caja y turno para el ticket
				caja: values.caja,
				turno: values.turno,
				// Marcar como borrador
				isDraft: true,
			};

			let savedDraft;

			if (isInvoiceEditing) {
				// Actualizar factura existente como borrador
				savedDraft = await updateInvoice(invoice.id, invoiceData);
				window.messageApi.success("Borrador actualizado correctamente");
			} else {
				// Crear nuevo borrador
				savedDraft = await saveInvoiceAsDraft(invoiceData);

				// Guardar líneas (filtrando líneas vacías e incompletas)
				const validLines = lines.filter(
					(line) =>
						line.key !== "new" && // Excluir la línea temporal
						line.No &&
						line.Description
				);

				for (const line of validLines) {
					await addInvoiceLine({
						...line,
						invoiceId: savedDraft.id,
						Document_Type: savedDraft.Document_Type,
					});
				}

				// Guardar líneas de pago
				for (const line of paymentLines) {
					await addInvoicePaymentLine({
						...line,
						invoiceId: savedDraft.id,
						Document_Type: savedDraft.Document_Type,
						Document_No_: savedDraft.No,
						Line_No: line.Line_No || paymentLines.indexOf(line) + 1,
					});
				}

				window.messageApi.success("Borrador guardado correctamente");
			}

			// Notificar que se ha guardado
			onSave(savedDraft);
		} catch (error) {
			console.error("Error al guardar borrador:", error);
			window.messageApi.error("No se pudo guardar el borrador");
		} finally {
			setLoading(false);
		}
	};

	const handleConfirmationSubmit = async (confirmedValues) => {
		try {
			setLoading(true);

			// Verificar si el cliente seleccionado está sincronizado
			const clientId = form.getFieldValue("Sell_to_Customer_No_");

			const selectedClient = clients.find(
				(c) => c.id === clientId || c.No === clientId
			);

			// Si el cliente no tiene No (no está sincronizado con BC)
			if (selectedClient && !selectedClient.No && isNewClient) {
				Modal.warning({
					title: "Cliente no sincronizado",
					content:
						"El cliente seleccionado aún no está sincronizado con Business Central. " +
						"La factura se guardará, pero no podrá sincronizarse hasta que el cliente " +
						"tenga un número asignado por Business Central.",
					okText: "Entendido",
				});
			}

			// Combinar los datos originales del formulario con los datos confirmados
			const finalInvoiceData = {
				...confirmationData,
				// Sobrescribir con los datos de facturación confirmados
				Bill_to_Name: confirmedValues.Bill_to_Name,
				Bill_to_Address: confirmedValues.Bill_to_Address,
				Direccion_2: confirmedValues.Direccion_2,
				Bill_to_City: confirmedValues.Bill_to_City,
				VAT_Registration_No_: confirmedValues.VAT_Registration_No_,
				Bill_to_Country_Region_Code:
					confirmedValues.Bill_to_Country_Region_Code,
				Tipo_Documento_SrI: confirmedValues.Tipo_Documento_SrI,
				Ship_to_Code: confirmedValues.Ship_to_Code,
			};

			// Formatear datos de la factura
			const invoiceData = {
				...finalInvoiceData,
				Document_Type: finalInvoiceData.Document_Type || "Invoice",
				Posting_Date: finalInvoiceData.Posting_Date?.format("YYYY-MM-DD"),
				Document_Date: finalInvoiceData.Document_Date?.format("YYYY-MM-DD"),
				// Para abonos, asegurarse de guardar el tipo de documento corregido
				Applies_to_Doc_Type:
					finalInvoiceData.Document_Type === "Credit Memo"
						? "Invoice"
						: undefined,
				// Guardar caja y turno para el ticket
				caja: finalInvoiceData.caja,
				turno: finalInvoiceData.turno,
				// Asegurarse de que no sea borrador
				isDraft: false,
			};

			let savedInvoice;

			if (isInvoiceEditing) {
				if (isDraft) {
					// Si estamos editando un borrador, convertirlo a factura normal
					await convertDraftToInvoice(invoice.id);
				}

				// Actualizar factura existente
				savedInvoice = await updateInvoice(invoice.id, invoiceData);

				window.messageApi.success("Factura actualizada correctamente");
			} else {
				// Crear nueva factura - el número se generará automáticamente
				savedInvoice = await addInvoice(invoiceData);

				// Guardar líneas (filtrando líneas vacías e incompletas)
				const validLines = lines.filter(
					(line) =>
						line.key !== "new" && // Excluir la línea temporal
						line.No &&
						line.Description
				);

				for (const line of validLines) {
					await addInvoiceLine({
						...line,
						invoiceId: savedInvoice.id,
						Document_Type: savedInvoice.Document_Type,
					});
				}

				// Guardar líneas de pago
				for (const line of paymentLines) {
					await addInvoicePaymentLine({
						...line,
						invoiceId: savedInvoice.id,
						Document_Type: savedInvoice.Document_Type,
						Document_No_: savedInvoice.No,
						Line_No: line.Line_No || paymentLines.indexOf(line) + 1,
					});
				}

				window.messageApi.success("Factura creada correctamente");
			}

			// Guardar la factura creada y mostrar el ticket
			setCreatedInvoice(savedInvoice);

			// Cerrar el modal de confirmación
			setConfirmationModalVisible(false);

			// Mostrar el ticket para impresión
			setTicketModalVisible(true);

			// Notificamos solo si el ticket se cierra sin imprimir
			// onSave(savedInvoice);
		} catch (error) {
			console.error("Error al guardar factura:", error);
			window.messageApi.error("No se pudo guardar la factura");
		} finally {
			setLoading(false);
		}
	};

	// Funciones para líneas de pago
	const showAddPaymentLineModal = () => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden añadir pagos a una factura sincronizada con Business Central"
			);
			return;
		}

		const isAbono = form.getFieldValue("Document_Type") === "Credit Memo";

		// Si es una nota de crédito, no permitir añadir líneas de pago manualmente
		if (isAbono) {
			window.messageApi.error(
				"Las líneas de pago en notas de crédito se importan automáticamente de la factura original y no pueden modificarse manualmente"
			);
			return;
		}

		// Calcular el importe pendiente para pasarlo al formulario de pago
		// Si ya hay pagos existentes, usar el total original sin descuentos
		// porque al añadir un segundo pago se eliminará el descuento
		const totalToUse =
			paymentLines.length > 0 ? invoiceTotals.total : calculateTotals().total;
		const paymentTotal = calculatePaymentTotal();

		const pendingAmount = totalToUse - paymentTotal;

		setSelectedPaymentLine({
			// Para todos los casos, el importe debe ser positivo
			pendingAmount: pendingAmount > 0 ? pendingAmount : 0,
			// Indicar si es un abono para que el formulario de pago lo maneje correctamente
			isAbono: false,
			// Indicar si ya hay líneas de pago existentes para prevenir descuentos
			hasExistingPayments: paymentLines.length > 0,
			// Pasar el número total de líneas de pago que existirán después de añadir esta
			totalPaymentLines: paymentLines.length + 1,
		});
		setPaymentLineModalVisible(true);
	};

	const showEditPaymentLineModal = (line) => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden editar pagos de una factura sincronizada con Business Central"
			);
			return;
		}

		const isAbono = form.getFieldValue("Document_Type") === "Credit Memo";

		// Si es una nota de crédito, no permitir editar líneas de pago
		if (isAbono) {
			window.messageApi.error(
				"Las líneas de pago en notas de crédito se importan automáticamente de la factura original y no pueden modificarse"
			);
			return;
		}

		setSelectedPaymentLine({
			...line,
			isAbono: false,
			// Indicar si hay otras líneas de pago además de esta
			hasExistingPayments: paymentLines.length > 1,
			// Pasar el número total de líneas de pago
			totalPaymentLines: paymentLines.length,
		});
		setPaymentLineModalVisible(true);
	};

	const handleAddPaymentLine = async (lineData) => {
		// Preparar los datos de la línea
		const processedLineData = {
			...lineData,
			// Si es un abono, no necesitamos ajustar el signo, ya que los importes ahora son positivos
		};

		if (!isInvoiceEditing) {
			// Si estamos creando una nueva factura, agregar la línea al array local
			setPaymentLines([
				...paymentLines,
				{ ...processedLineData, id: Date.now() },
			]);
			setPaymentLineModalVisible(false);
			return;
		}

		try {
			setPaymentLineLoading(true);
			const newLine = await addInvoicePaymentLine({
				...processedLineData,
				invoiceId: invoice.id,
				Document_Type: invoice.Document_Type || "Invoice",
				Document_No_: invoice.No,
				Line_No: paymentLines.length + 1,
			});

			setPaymentLines([...paymentLines, newLine]);
			setPaymentLineModalVisible(false);
			window.messageApi.success("Línea de pago agregada correctamente");
		} catch (error) {
			console.error("Error al agregar línea de pago:", error);
			window.messageApi.error("No se pudo agregar la línea de pago");
		} finally {
			setPaymentLineLoading(false);
		}
	};

	const handleUpdatePaymentLine = async (updatedLine) => {
		// Preparar los datos de la línea
		const processedLine = {
			...updatedLine,
			// Si es un abono, no necesitamos ajustar el signo, ya que los importes ahora son positivos
		};

		if (!isInvoiceEditing) {
			// Si estamos creando una nueva factura, actualizar la línea en el array local
			const updatedLines = paymentLines.map((line) =>
				line.id === processedLine.id ? processedLine : line
			);
			setPaymentLines(updatedLines);
			setPaymentLineModalVisible(false);
			return;
		}

		try {
			setPaymentLineLoading(true);
			await updateInvoicePaymentLine(processedLine.id, processedLine);

			const updatedLines = paymentLines.map((line) =>
				line.id === processedLine.id ? processedLine : line
			);
			setPaymentLines(updatedLines);
			setPaymentLineModalVisible(false);
			window.messageApi.success("Línea de pago actualizada correctamente");
		} catch (error) {
			console.error("Error al actualizar línea de pago:", error);
			window.messageApi.error("No se pudo actualizar la línea de pago");
		} finally {
			setPaymentLineLoading(false);
		}
	};

	const handleDeletePaymentLine = async (line) => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden eliminar pagos de una factura sincronizada con Business Central"
			);
			return;
		}

		const isAbono = form.getFieldValue("Document_Type") === "Credit Memo";

		// Si es una nota de crédito, no permitir eliminar líneas de pago
		if (isAbono) {
			window.messageApi.error(
				"Las líneas de pago en notas de crédito se importan automáticamente de la factura original y no pueden eliminarse"
			);
			return;
		}

		if (!isInvoiceEditing) {
			// Si estamos creando una nueva factura, eliminar la línea del array local
			setPaymentLines(paymentLines.filter((l) => l.id !== line.id));
			return;
		}

		Modal.confirm({
			title: "¿Está seguro de eliminar esta línea de pago?",
			content: "Esta acción no se puede deshacer",
			okText: "Eliminar",
			okType: "danger",
			cancelText: "Cancelar",
			onOk: async () => {
				try {
					setPaymentLineLoading(true);
					await deleteInvoicePaymentLine(line.id);

					setPaymentLines(paymentLines.filter((l) => l.id !== line.id));
					window.messageApi.success("Línea de pago eliminada correctamente");
				} catch (error) {
					console.error("Error al eliminar línea de pago:", error);
					window.messageApi.error("No se pudo eliminar la línea de pago");
				} finally {
					setPaymentLineLoading(false);
				}
			},
		});
	};

	const paymentColumns = [
		{
			title: "Línea",
			dataIndex: "Line_No",
			key: "Line_No",
			render: (_, __, index) => index + 1,
		},
		{
			title: "Forma de pago",
			dataIndex: "Payment_Method",
			key: "Payment_Method",
			render: (method, record, index, data) => {
				const hasMultiplePayments = paymentLines.length > 1;

				return (
					<div>
						<span>
							{method}
							{record.Dto > 0 && (
								<Tag
									color={hasMultiplePayments ? "orange" : "green"}
									style={{ marginLeft: 8 }}
									title={
										hasMultiplePayments
											? "Este descuento no se aplica debido a múltiples formas de pago"
											: "Descuento aplicado"
									}
								>
									Dto: {record.Dto}%
									{!hasMultiplePayments &&
										` (-${(record.Dto_Amount || 0).toFixed(2)} $)`}
									{hasMultiplePayments && " (No aplicado)"}
								</Tag>
							)}
							{record.isAutoGenerated && (
								<Tag color="blue" style={{ marginLeft: 8 }}>
									Automático
								</Tag>
							)}
						</span>
						{/* Mostrar información del cambio para pagos en efectivo */}
						{record.importeRecibido && record.cambio !== undefined && (
							<div style={{ marginTop: 4, fontSize: "12px", color: "#666" }}>
								<div>Recibido: {record.importeRecibido.toFixed(2)} $</div>
								{record.cambio > 0 && (
									<div style={{ color: "#52c41a", fontWeight: "bold" }}>
										Cambio: {record.cambio.toFixed(2)} $
									</div>
								)}
							</div>
						)}
					</div>
				);
			},
		},
		{
			title: "Importe",
			dataIndex: "Amount",
			key: "Amount",
			render: (amount) => `${amount?.toFixed(2)} $`,
		},
		{
			title: "Acciones",
			key: "actions",
			render: (_, record) => {
				const isAbono = form.getFieldValue("Document_Type") === "Credit Memo";
				const isDisabled = isSynchronized || isAbono;

				return (
					<div className="line-actions">
						<Button
							icon={<EditOutlined />}
							size="small"
							onClick={() => showEditPaymentLineModal(record)}
							disabled={isDisabled}
							title={
								isSynchronized
									? "No se pueden editar pagos de facturas sincronizadas"
									: isAbono
										? "Las líneas de pago en notas de crédito se importan automáticamente y no pueden editarse"
										: "Editar pago"
							}
						/>
						<Button
							icon={<DeleteOutlined />}
							size="small"
							danger
							onClick={() => handleDeletePaymentLine(record)}
							disabled={isDisabled}
							title={
								isSynchronized
									? "No se pueden eliminar pagos de facturas sincronizadas"
									: isAbono
										? "Las líneas de pago en notas de crédito se importan automáticamente y no pueden eliminarse"
										: "Eliminar pago"
							}
						/>
					</div>
				);
			},
		},
	];

	return (
		<div className="invoice-form-container">
			<h2>
				{isInvoiceEditing
					? `Editar ${documentType === "Credit Memo" ? "nota de crédito" : "factura"
					}`
					: `Nueva ${documentType === "Credit Memo" ? "nota de crédito" : "factura"
					}`}
				{isDraft ? " (Borrador)" : ""}
			</h2>

			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				initialValues={{
					Document_Type: "Invoice",
					Posting_Date: dayjs(),
					Document_Date: dayjs(),
				}}
			>
				<Card className="invoice-header-section">
					<h3>
						Datos de{" "}
						{documentType === "Credit Memo" ? "nota de crédito" : "factura"}
					</h3>
					<Row gutter={16}>
						<Col xs={24} sm={8}>
							<Form.Item
								name="Document_Type"
								label="Tipo de documento"
								rules={[{ required: true, message: "Seleccione un tipo" }]}
							>
								<Select disabled>
									<Option value="Invoice">Factura</Option>
									<Option value="Credit Memo">Notas de crédito</Option>
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={16}>
							<Form.Item
								name="No"
								label="Nº Factura"
								tooltip="Número asignado por Business Central después de sincronizar"
							>
								<Input disabled placeholder="Pendiente de sincronización" />
							</Form.Item>
						</Col>
					</Row>

					{documentType === "Credit Memo" && (
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									name="Applies_to_Doc_No"
									label="Nº Factura Corregida"
									tooltip="Número de la factura a la que se aplica esta nota de crédito"
									rules={[
										{
											required: true,
											message:
												"Indique la factura a la que se aplica esta nota de crédito",
										},
									]}
								>
									<Input
										disabled
										placeholder="Introduzca el número de la factura original"
									/>
								</Form.Item>
							</Col>
						</Row>
					)}

					<Row gutter={16}>
						<Col xs={24} sm={12}>
							<Form.Item
								name="Posting_Date"
								label="Fecha de registro"
								rules={[{ required: true, message: "Seleccione una fecha" }]}
							>
								<DatePicker
									disabled={true}
									style={{ width: "100%" }}
									format="DD/MM/YYYY"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								name="Document_Date"
								label="Fecha de documento"
								rules={[{ required: true, message: "Seleccione una fecha" }]}
							>
								<DatePicker
									disabled={true}
									style={{ width: "100%" }}
									format="DD/MM/YYYY"
								/>
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col xs={24} sm={12}>
							{/* Campo oculto para mantener el valor real de la caja */}
							<Form.Item name="caja" hidden={true} />
							<Form.Item
								label="Caja"
								required={true}
								tooltip="La caja se toma automáticamente de la apertura de caja activa"
							>
								<Input
									disabled={true}
									value={cajaNombre || aperturaActiva?.Caja || ""}
									placeholder="No hay caja abierta"
									addonBefore={<BankOutlined />}
									suffix={
										aperturaActiva ? (
											<span style={{ color: "#52c41a" }}>Caja activa</span>
										) : (
											<span style={{ color: "#ff4d4f" }}>Sin apertura</span>
										)
									}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							{/* Campo oculto para mantener el valor real del turno */}
							<Form.Item name="turno" hidden={true} />
							<Form.Item
								label="Turno"
								required={true}
								tooltip="El turno se toma automáticamente de la apertura de caja activa"
							>
								<Input
									disabled={true}
									value={turnoNombre || aperturaActiva?.Turno || ""}
									placeholder="No hay turno activo"
									addonBefore={<ClockCircleOutlined />}
									suffix={
										aperturaActiva ? (
											<span style={{ color: "#52c41a" }}>Turno activo</span>
										) : (
											<span style={{ color: "#ff4d4f" }}>Sin apertura</span>
										)
									}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col span={24}>
							<Form.Item
								name="libros_sueltos"
								valuePropName="checked"
								tooltip="Se activa automáticamente cuando la factura contiene productos tipo kit. También puede activarse manualmente."
							>
								<Checkbox>
									El cliente realiza la compra de libros sueltos y no kit
									{hasKitsInLines && (
										<Tag
											color="orange"
											style={{ marginLeft: 8 }}
											title="Se detectaron productos tipo kit en las líneas de factura"
										>
											Kit detectado
										</Tag>
									)}
								</Checkbox>
							</Form.Item>
						</Col>
					</Row>

					<Divider orientation="left">Cliente</Divider>

					<Row gutter={16}>
						<Col xs={20} sm={20}>
							<Form.Item
								name="Sell_to_Customer_No_"
								label="Cliente"
								rules={[{ required: true, message: "Seleccione un cliente" }]}
							>
								<Select
									showSearch
									placeholder="Seleccione un cliente o busque por nombre/DNI"
									optionFilterProp="children"
									loading={loading}
									onChange={handleClientChange}
									filterOption={(input, option) => {
										const client = clients.find((c) => c.id === option.value);
										return (
											(option?.label || "")
												.toLowerCase()
												.includes(input.toLowerCase()) ||
											(client?.Numero_Identificacion_fiscal || "")
												.toLowerCase()
												.includes(input.toLowerCase())
										);
									}}
								>
									{clients.map((client) => (
										<Option
											key={client.id}
											value={client.id}
											label={`${client.No || client.id} - ${client.name}${client.Numero_Identificacion_fiscal
												? ` - ${client.Numero_Identificacion_fiscal}`
												: ""
												}`}
										>
											{client.No || client.id} - {client.name}
											{client.Numero_Identificacion_fiscal && (
												<span style={{ marginLeft: 8 }}>
													Identificador: {client.Numero_Identificacion_fiscal}
												</span>
											)}
											{!client.No && (
												<span style={{ color: "#ff4d4f" }}>
													{" "}
													(Sin sincronizar)
												</span>
											)}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col
							xs={4}
							sm={4}
							style={{
								display: "flex",
								alignItems: "end",
								paddingBottom: "24px",
							}}
						>
							<Button
								type="primary"
								icon={<UserAddOutlined />}
								onClick={handleAddClient}
								title="Añadir nuevo cliente"
								style={{ width: "100%" }}
							/>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col xs={24} sm={24}>
							<Form.Item
								name="Colegio"
								label="Colegio"
								rules={[{ required: true, message: "Seleccione un colegio" }]}
							>
								<Select
									showSearch
									placeholder="Seleccione un colegio (opcional)"
									optionFilterProp="children"
									loading={loading}
									allowClear
									filterOption={(input, option) =>
										(option?.label || "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									onChange={handleColegioChange}
								>
									{colegios.map((colegio) => (
										<Option
											key={colegio.No}
											value={colegio.No}
											label={`${colegio.No} - ${colegio.nombre}`}
										>
											{colegio.No} - {colegio.nombre}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col xs={24}>
							<Form.Item
								name="Ship_to_Code"
								label="Dirección de envío"
								tooltip="Seleccione una dirección de envío del cliente"
							>
								<Select
									showSearch
									placeholder={
										isNewClient
											? "Cliente nuevo - Sin direcciones disponibles"
											: clientAddresses.length === 0
												? "Cliente sin direcciones registradas"
												: "Seleccione una dirección de envío"
									}
									optionFilterProp="children"
									allowClear={false}
									disabled={!clientAddresses.length || isNewClient}
									filterOption={(input, option) =>
										(option?.label || "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
								>
									{clientAddresses.map((address) => (
										<Option
											key={address.Code}
											value={address.Code}
											label={`${address.Code} - ${address.Name} - ${address.Ciudad}, ${address.Provincia}`}
										>
											<div>
												<strong>{address.Code}</strong> - {address.Name}
											</div>
										</Option>
									))}
								</Select>
							</Form.Item>
							{isNewClient && (
								<div style={{ marginTop: "-16px", marginBottom: "16px" }}>
									<small style={{ color: "#ff4d4f" }}>
										⚠️ El cliente seleccionado no está sincronizado con Business
										Central. Las direcciones de envío no están disponibles para
										clientes nuevos.
									</small>
								</div>
							)}
							{!isNewClient && clientAddresses.length === 0 && (
								<div style={{ marginTop: "-16px", marginBottom: "16px" }}>
									<small style={{ color: "#ff4d4f" }}>
										⚠️ Este cliente no tiene direcciones de envío registradas.
										Debe agregar al menos una dirección para poder crear la
										factura.
									</small>
								</div>
							)}
							{!isNewClient && clientAddresses.length === 1 && (
								<div style={{ marginTop: "-16px", marginBottom: "16px" }}>
									<small style={{ color: "#52c41a" }}>
										✓ Dirección seleccionada automáticamente (única dirección
										disponible).
									</small>
								</div>
							)}
						</Col>
					</Row>

					<Collapse
						style={{ marginTop: 16 }}
						items={[
							{
								key: "billing-details",
								label: "Datos de facturación detallados",
								children: (
									<>
										<Row gutter={16}>
											<Col xs={24} sm={12}>
												<Form.Item
													name="Bill_to_Name"
													label="Nombre de facturación"
													rules={[
														{
															required: true,
															message: "Ingrese un nombre de facturación",
														},
													]}
												>
													<Input />
												</Form.Item>
											</Col>
											<Col xs={24} sm={12}>
												<Form.Item
													name="Bill_to_Address"
													label="Dirección de facturación"
													rules={[
														{
															required: true,
															message: "Ingrese una dirección de facturación",
														},
													]}
												>
													<Input />
												</Form.Item>
											</Col>
										</Row>

										<Row gutter={16}>
											<Col xs={24}>
												<Form.Item name="Direccion_2" label="Envio a Colonia 2">
													<Input placeholder="Dirección secundaria o información adicional de envío" />
												</Form.Item>
											</Col>
										</Row>

										<Row gutter={16}>
											<Col xs={24} sm={12}>
												<Form.Item
													name="Bill_to_City"
													label="Ciudad"
													rules={[
														{ required: true, message: "Ingrese una ciudad" },
													]}
												>
													<Input />
												</Form.Item>
											</Col>
											<Col xs={24} sm={4}>
												<Form.Item
													name="Tipo_Documento_SrI"
													label="Tipo Documento"
													rules={[
														{
															required: true,
															message: "Seleccione el tipo de documento",
														},
													]}
												>
													<Select
														placeholder="Tipo"
														options={[
															{ value: "RUC", label: "RUC" },
															{ value: "Cedula", label: "Cédula" },
															{ value: "Pasaporte", label: "Pasaporte" },
														]}
													/>
												</Form.Item>
											</Col>
											<Col xs={24} sm={8}>
												<Form.Item
													name="VAT_Registration_No_"
													label={
														tipoDocumentoSrI === "RUC"
															? "RUC"
															: tipoDocumentoSrI === "Cedula"
																? "Cédula"
																: tipoDocumentoSrI === "Pasaporte"
																	? "Pasaporte"
																	: "Número de identificación"
													}
													rules={[
														{
															required: true,
															message: `Ingrese el ${tipoDocumentoSrI === "RUC"
																? "RUC"
																: tipoDocumentoSrI === "Cedula"
																	? "número de cédula"
																	: tipoDocumentoSrI === "Pasaporte"
																		? "número de pasaporte"
																		: "número de identificación"
																}`,
														},
													]}
												>
													<Input />
												</Form.Item>
											</Col>
										</Row>

										<Row gutter={16}>
											<Col xs={24} sm={4}>
												<Form.Item
													name="Bill_to_Country_Region_Code"
													label="País"
													style={{ display: "none" }}
													rules={[
														{ required: true, message: "Ingrese un país" },
													]}
												>
													<Input disabled={true} />
												</Form.Item>
											</Col>
										</Row>
									</>
								),
							},
						]}
					/>

					<Divider orientation="left">Cupón de descuento y Campaña</Divider>

					<Row gutter={16}>
						<Col xs={24} sm={16}>
							<Form.Item
								name="couponDetail"
								label="Detalle de cupón aplicado"
								tooltip="Seleccione un detalle de cupón válido para aplicar a esta factura"
							>
								<Select
									showSearch
									placeholder="Seleccionar detalle de cupón (opcional)"
									optionFilterProp="children"
									loading={loadingCoupons}
									onChange={handleCouponChange}
									allowClear
									disabled={isSynchronized}
									filterOption={(input, option) =>
										(option?.label || "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
								>
									{availableCouponDetails.map((couponDetail) => (
										<Select.Option
											key={couponDetail.key}
											value={couponDetail.key}
											disabled={!couponDetail.isValid}
											label={couponDetail.label}
										>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<span>
													{couponDetail.couponNo} -{" "}
													{couponDetail.couponDescription}
													<br />
													<small style={{ color: "#666" }}>
														{couponDetail.detailType}: {couponDetail.detailNo}
													</small>
												</span>
											</div>
											{!couponDetail.isValid && (
												<div
													style={{
														fontSize: "12px",
														color: "#ff4d4f",
														marginTop: "4px",
													}}
												>
													{couponDetail.validation?.detailValidation?.reason ||
														couponDetail.validation?.couponValidation?.reason}
												</div>
											)}
											{couponDetail.isValid && (
												<div
													style={{
														fontSize: "12px",
														color: "#52c41a",
														marginTop: "4px",
													}}
												>
													Válido para {couponDetail.detailType}:{" "}
													{couponDetail.detailNo}
												</div>
											)}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							{selectedCouponDetail && (
								<div
									style={{
										padding: "8px 12px",
										backgroundColor: "#f6ffed",
										border: "1px solid #b7eb8f",
										borderRadius: "6px",
										marginTop: "30px",
									}}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}
									>
										<TagOutlined style={{ color: "#52c41a" }} />
										<span style={{ fontWeight: "bold", color: "#52c41a" }}>
											Detalle de cupón seleccionado
										</span>
									</div>
									<div
										style={{
											fontSize: "12px",
											color: "#666",
											marginTop: "4px",
										}}
									>
										<strong>Cupón:</strong> {selectedCouponDetail.couponNo} -{" "}
										{selectedCouponDetail.couponDescription}
									</div>
									<div style={{ fontSize: "12px", color: "#666" }}>
										<strong>Detalle:</strong> {selectedCouponDetail.detailType}:{" "}
										{selectedCouponDetail.detailNo}
									</div>
								</div>
							)}
						</Col>
					</Row>

					<Row gutter={16}>
						<Col xs={24} sm={16}>
							<Form.Item
								name="Campaign_No_"
								label="Campaña aplicada"
								tooltip="Seleccione una campaña válida para aplicar a esta factura"
							// rules={[{ required: true, message: "Seleccione una campaña" }]}
							>
								<Select
									showSearch
									placeholder="Seleccionar campaña (opcional)"
									optionFilterProp="children"
									loading={loadingCampaigns}
									allowClear
									disabled={true}
									filterOption={(input, option) =>
										(option?.label || "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
								>
									{campaigns.map((campaign) => (
										<Select.Option
											key={campaign.No}
											value={campaign.No}
											label={`${campaign.No} - ${campaign.Description || "Sin descripción"
												}`}
										>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<span>
													{campaign.No} -{" "}
													{campaign.Description || "Sin descripción"}
												</span>
											</div>
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							{form.getFieldValue("Campaign_No_") && (
								<div
									style={{
										padding: "8px 12px",
										backgroundColor: "#e6f7ff",
										border: "1px solid #91d5ff",
										borderRadius: "6px",
										marginTop: "30px",
									}}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}
									>
										<TagOutlined style={{ color: "#1890ff" }} />
										<span style={{ fontWeight: "bold", color: "#1890ff" }}>
											Campaña seleccionada
										</span>
									</div>
									<div
										style={{
											fontSize: "12px",
											color: "#666",
											marginTop: "4px",
										}}
									>
										<strong>Campaña:</strong>{" "}
										{form.getFieldValue("Campaign_No_")}
									</div>
									{campaigns.find(
										(c) => c.No === form.getFieldValue("Campaign_No_")
									)?.Description && (
											<div style={{ fontSize: "12px", color: "#666" }}>
												<strong>Descripción:</strong>{" "}
												{
													campaigns.find(
														(c) => c.No === form.getFieldValue("Campaign_No_")
													)?.Description
												}
											</div>
										)}
									{(() => {
										const selectedCampaign = campaigns.find(
											(c) => c.No === form.getFieldValue("Campaign_No_")
										);
										return (
											selectedCampaign &&
											(selectedCampaign.Starting_Date ||
												selectedCampaign.Ending_Date) && (
												<div style={{ fontSize: "12px", color: "#666" }}>
													<strong>Fechas:</strong>{" "}
													{selectedCampaign.Starting_Date || "Sin fecha inicio"}{" "}
													- {selectedCampaign.Ending_Date || "Sin fecha fin"}
												</div>
											)
										);
									})()}
								</div>
							)}
						</Col>
					</Row>
				</Card>

				<Card className="invoice-lines-section">
					<h3>Líneas de factura</h3>

					<InvoiceLines
						invoice={invoice}
						lines={lines}
						setLines={setLines}
						products={products}
						isSynchronized={isSynchronized}
						isInvoiceEditing={isInvoiceEditing}
						selectedClientDiscount={selectedClientDiscount}
						onTotalsChange={setInvoiceTotals}
						loading={lineLoading}
						selectedClient={clients?.find(
							(c) => c.id === form.getFieldValue("Sell_to_Customer_No_")
						)}
						selectedColegio={form.getFieldValue("Colegio")}
						selectedCouponDetail={selectedCouponDetail}
						onPaymentLinesUpdate={setPaymentLines}
						documentType={documentType}
					/>
				</Card>

				<Card className="invoice-payments-section">
					<h3>Pagos</h3>

					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={showAddPaymentLineModal}
						className="add-payment-button"
						disabled={
							isSynchronized ||
							form.getFieldValue("Document_Type") === "Credit Memo"
						}
						title={
							isSynchronized
								? "No se pueden añadir pagos a facturas sincronizadas"
								: form.getFieldValue("Document_Type") === "Credit Memo"
									? "Las líneas de pago en notas de crédito se importan automáticamente"
									: "Añadir pago"
						}
					>
						Añadir pago
					</Button>

					<Table
						columns={paymentColumns}
						dataSource={paymentLines}
						rowKey="id"
						loading={paymentLineLoading}
						className="invoice-payment-lines-table"
						scroll={{ x: "max-content" }}
					/>

					<div className="invoice-totals">
						<div className="invoice-totals-item grand-total">
							<span>Total pagado:</span>
							<span>{calculatePaymentTotal().toFixed(2)} $</span>
						</div>
						{calculateDiscountFromPaymentMethod() > 0 && (
							<div className="invoice-totals-item">
								<span>Descuento forma de pago:</span>
								<span>{calculateDiscountFromPaymentMethod()}%</span>
							</div>
						)}
						{paymentLines.length > 1 && (
							<div className="invoice-totals-item">
								<span>Descuento forma de pago:</span>
								<span style={{ color: "#ff4d4f" }}>
									No aplicable con múltiples formas de pago
								</span>
							</div>
						)}
						<div className="invoice-totals-item grand-total">
							<span>Pendiente:</span>
							<span
								className={
									form.getFieldValue("Document_Type") === "Credit Memo" &&
										Math.abs(applyPaymentMethodDiscount(invoiceTotals.total)) -
										calculatePaymentTotal() <
										0
										? "negative-amount"
										: ""
								}
							>
								{form.getFieldValue("Document_Type") === "Credit Memo"
									? (
										Math.abs(
											applyPaymentMethodDiscount(invoiceTotals.total)
										) - calculatePaymentTotal()
									).toFixed(2)
									: (
										applyPaymentMethodDiscount(invoiceTotals.total) -
										calculatePaymentTotal()
									).toFixed(2)}{" "}
								$
							</span>
						</div>
					</div>
				</Card>

				<div className="invoice-form-actions">
					<Button onClick={onCancel}>Cancelar</Button>
					<Button
						icon={<SaveOutlined />}
						onClick={handleSaveAsDraft}
						loading={loading}
						disabled={
							isSynchronized ||
							(form.getFieldValue("Document_Type") === "Credit Memo" &&
								!isApproximatelyZero(
									Math.abs(applyPaymentMethodDiscount(invoiceTotals.total)) -
									calculatePaymentTotal()
								)
							)
						}
						title={
							isSynchronized
								? "No se pueden modificar facturas sincronizadas"
								: form.getFieldValue("Document_Type") === "Credit Memo" &&
									!isApproximatelyZero(
										Math.abs(applyPaymentMethodDiscount(invoiceTotals.total)) -
										calculatePaymentTotal()
									)
									? "Las notas de crédito requieren que el pendiente sea 0.00"
									: "Guardar como borrador de " +
									(documentType === "Credit Memo"
										? "nota de crédito"
										: "factura")
						}
					>
						Guardar como borrador de{" "}
						{documentType === "Credit Memo" ? "nota de crédito" : "factura"}
					</Button>
					<Button
						type="primary"
						htmlType="submit"
						loading={loading}
						disabled={
							isSynchronized ||
							(form.getFieldValue("Document_Type") === "Credit Memo"
								? !isApproximatelyZero(
									Math.abs(applyPaymentMethodDiscount(invoiceTotals.total)) -
									calculatePaymentTotal()
								)
								: applyPaymentMethodDiscount(invoiceTotals.total) -
								calculatePaymentTotal() >
								0)
						}
						title={
							isSynchronized
								? "No se pueden modificar facturas sincronizadas"
								: form.getFieldValue("Document_Type") === "Credit Memo"
									? !isApproximatelyZero(
										Math.abs(applyPaymentMethodDiscount(invoiceTotals.total)) -
										calculatePaymentTotal()
									)
										? "Las notas de crédito requieren que el pendiente sea 0.00"
										: ""
									: applyPaymentMethodDiscount(invoiceTotals.total) -
										calculatePaymentTotal() >
										0
										? "Debe completar todos los pagos antes de finalizar"
										: ""
						}
					>
						{isInvoiceEditing
							? isDraft
								? "Finalizar y guardar"
								: "Actualizar " +
								(documentType === "Credit Memo"
									? "nota de crédito"
									: "factura")
							: "Crear " +
							(documentType === "Credit Memo"
								? "nota de crédito"
								: "factura")}
					</Button>
				</div>
			</Form>

			{/* Modal de confirmación de datos del cliente (requerido por ley) */}
			<Modal
				title={`Confirmar datos de ${confirmationData?.Document_Type === "Credit Memo"
					? "nota de crédito"
					: "facturación"
					}`}
				open={confirmationModalVisible}
				onCancel={() => setConfirmationModalVisible(false)}
				footer={null}
				width={700}
			>
				<div className="confirmation-modal-content">
					<p className="legal-notice">
						Por requisitos legales, es necesario confirmar los datos de
						facturación del cliente. Por favor, verifique que todos los datos
						son correctos antes de continuar.
					</p>

					<Form
						form={confirmForm}
						layout="vertical"
						onFinish={handleConfirmationSubmit}
					>
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									name="Bill_to_Name"
									label="Nombre de facturación"
									rules={[
										{ required: true, message: "Este campo es obligatorio" },
									]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									name="Bill_to_Address"
									label="Dirección de facturación"
									rules={[
										{ required: true, message: "Este campo es obligatorio" },
									]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									name="Direccion_2"
									label="Envio a Colonia 2"
								>
									<Input placeholder="Dirección secundaria o información adicional de envío" />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									name="Bill_to_City"
									label="Población"
									rules={[
										{ required: true, message: "Este campo es obligatorio" },
									]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<ConfirmationFormFields form={confirmForm} />

						<Row gutter={16}>
							<Col span={12}>
								<Form.Item
									name="Bill_to_Country_Region_Code"
									label="País"
									style={{ display: "none" }}
									rules={[
										{ required: true, message: "Este campo es obligatorio" },
									]}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									name="Ship_to_Code"
									label="Dirección de envío"
									tooltip="Código de dirección de envío (opcional)"
								>
									<Input
										disabled
										placeholder="Sin dirección de envío seleccionada"
									/>
								</Form.Item>
							</Col>
						</Row>

						<div className="confirmation-actions">
							<Button onClick={() => setConfirmationModalVisible(false)}>
								Cancelar
							</Button>
							<Button type="primary" htmlType="submit" loading={loading}>
								Confirmar y {isInvoiceEditing ? "Actualizar" : "Crear"}{" "}
								{confirmationData?.Document_Type === "Credit Memo"
									? "Nota de crédito"
									: "Factura"}
							</Button>
						</div>
					</Form>
				</div>
			</Modal>

			<Modal
				title={selectedPaymentLine ? "Editar pago" : "Añadir pago"}
				open={paymentLineModalVisible}
				onCancel={() => setPaymentLineModalVisible(false)}
				footer={null}
				width={700}
			>
				<InvoicePaymentLineForm
					line={selectedPaymentLine}
					onAddLine={handleAddPaymentLine}
					onUpdateLine={handleUpdatePaymentLine}
					onCancel={() => setPaymentLineModalVisible(false)}
				/>
			</Modal>

			{/* Modal del ticket */}
			<InvoiceTicket
				open={ticketModalVisible}
				onClose={() => {
					setTicketModalVisible(false);
					// Al cerrar el ticket, notificar al componente padre
					onSave(createdInvoice);
				}}
				invoice={createdInvoice}
				lines={lines}
				caja={createdInvoice?.caja || form.getFieldValue("caja")}
				turno={createdInvoice?.turno || form.getFieldValue("turno")}
				paymentLines={paymentLines}
				selectedClient={clients?.find((c) => {
					const val = form.getFieldValue("Sell_to_Customer_No_");
					return c.id === val || c.No === val;
				})}
				tpvNo={tpv}
			/>

			{/* Modal para crear nuevo cliente */}
			<Modal
				title="Nuevo Cliente"
				open={clientModalVisible}
				onCancel={() => setClientModalVisible(false)}
				footer={null}
				width={800}
			>
				{(() => {
					const fv = form.getFieldsValue();
					const initialData = {
						name: fv.Bill_to_Name || "",
						Direccion: fv.Bill_to_Address || "",
						Direccion_2: fv.Direccion_2 || "",
						Poblacion: fv.Bill_to_City || "",
						Pais: fv.Bill_to_Country_Region_Code || "",
						Numero_Identificacion_fiscal: fv.VAT_Registration_No_ || "",
						Tipo_Documento_SrI: fv.Tipo_Documento_SrI || "Cedula",
						E_Mail: fv.E_Mail || "",
						Telefono: fv.Telefono || "",
						Mobil: fv.Mobil || "",
						Customer_Price_Group: fv.Customer_Price_Group || "",
						Contacto: fv.Contacto || "",
						POS_Discount: 0,
					};
					return (
						<ClientForm
							initialData={initialData}
							skipAutoClearOnTipoChange={true}
							onClientAdded={handleClientAdded}
							onClientUpdated={() => setClientModalVisible(false)}
						/>
					);
				})()}
			</Modal>
		</div>
	);
};

export default InvoiceForm;
